import React from "react";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";
import {DEFAULT_CONSENT} from "@/utils/analytics";

// Constants for categories
const CAT_NECESSARY = "necessary";
const CAT_ANALYTICS = "analytics";
const CAT_ADVERTISEMENT = "advertisement";
const CAT_FUNCTIONALITY = "functionality";
const CAT_SECURITY = "security";

// Constants for services
const SERVICE_AD_STORAGE = "ad_storage";
const SERVICE_AD_USER_DATA = "ad_user_data";
const SERVICE_AD_PERSONALIZATION = "ad_personalization";
const SERVICE_ANALYTICS_STORAGE = "analytics_storage";
const SERVICE_FUNCTIONALITY_STORAGE = "functionality_storage";
const SERVICE_PERSONALIZATION_STORAGE = "personalization_storage";
const SERVICE_SECURITY_STORAGE = "security_storage";

// Helper function to update gtag consent
const updateGtagConsent = () => {
  window?.gtag?.("consent", "update", {
    [SERVICE_ANALYTICS_STORAGE]: CookieConsent.acceptedService(
      SERVICE_ANALYTICS_STORAGE,
      CAT_ANALYTICS,
    )
      ? "granted"
      : "denied",
    [SERVICE_AD_STORAGE]: CookieConsent.acceptedService(
      SERVICE_AD_STORAGE,
      CAT_ADVERTISEMENT,
    )
      ? "granted"
      : "denied",
    [SERVICE_AD_USER_DATA]: CookieConsent.acceptedService(
      SERVICE_AD_USER_DATA,
      CAT_ADVERTISEMENT,
    )
      ? "granted"
      : "denied",
    [SERVICE_AD_PERSONALIZATION]: CookieConsent.acceptedService(
      SERVICE_AD_PERSONALIZATION,
      CAT_ADVERTISEMENT,
    )
      ? "granted"
      : "denied",
    [SERVICE_FUNCTIONALITY_STORAGE]: CookieConsent.acceptedService(
      SERVICE_FUNCTIONALITY_STORAGE,
      CAT_FUNCTIONALITY,
    )
      ? "granted"
      : "denied",
    [SERVICE_PERSONALIZATION_STORAGE]: CookieConsent.acceptedService(
      SERVICE_PERSONALIZATION_STORAGE,
      CAT_FUNCTIONALITY,
    )
      ? "granted"
      : "denied",
    [SERVICE_SECURITY_STORAGE]: CookieConsent.acceptedService(
      SERVICE_SECURITY_STORAGE,
      CAT_SECURITY,
    )
      ? "granted"
      : "denied",
  });
};

export const useCookiesConsent = () => {
  React.useEffect(() => {
    // Set default consent (also set earlier in `initGoogleTag`; kept here as a
    // safety net in case the hook mounts before any `gtag` call has happened).
    window.gtag?.("consent", "default", DEFAULT_CONSENT);

    CookieConsent.run({
      categories: {
        [CAT_NECESSARY]: {
          enabled: true,
          readOnly: true,
        },
        [CAT_ANALYTICS]: {
          autoClear: {
            cookies: [{name: /^_ga/}, {name: "_gid"}],
          },
          services: {
            [SERVICE_ANALYTICS_STORAGE]: {
              label:
                "Umožňuje ukládání (například cookies) související s analytikou, např. délka návštěvy.",
            },
          },
        },
        [CAT_ADVERTISEMENT]: {
          services: {
            [SERVICE_AD_STORAGE]: {
              label:
                "Umožňuje ukládání (například cookies) související s reklamou.",
            },
            [SERVICE_AD_USER_DATA]: {
              label:
                "Nastavuje souhlas s odesíláním uživatelských dat souvisejících s reklamou do Google.",
            },
            [SERVICE_AD_PERSONALIZATION]: {
              label: "Nastavuje souhlas s personalizovanou reklamou.",
            },
          },
        },
        [CAT_FUNCTIONALITY]: {
          services: {
            [SERVICE_FUNCTIONALITY_STORAGE]: {
              label:
                "Umožňuje ukládání, které podporuje funkčnost webu nebo aplikace, např. nastavení jazyka.",
            },
            [SERVICE_PERSONALIZATION_STORAGE]: {
              label:
                "Umožňuje ukládání související s personalizací, např. doporučení videí.",
            },
          },
        },
        [CAT_SECURITY]: {
          services: {
            [SERVICE_SECURITY_STORAGE]: {
              label:
                "Umožňuje ukládání související s bezpečností, jako je autentizace, prevence podvodů a ochrana uživatelů.",
            },
          },
        },
      },

      onFirstConsent: updateGtagConsent,
      onConsent: updateGtagConsent,
      onChange: updateGtagConsent,

      language: {
        default: "cs",
        translations: {
          cs: {
            consentModal: {
              title: "Používáme cookies",
              description:
                "Tato webová stránka používá nezbytné cookies pro zajištění správného fungování a sledovací cookies pro pochopení toho, jak s ní interagujete. Ty druhé budou nastaveny pouze po souhlasu.",
              acceptAllBtn: "Přijmout vše",
              acceptNecessaryBtn: "Odmítnout vše",
              showPreferencesBtn: "Spravovat individuální preference",
            },
            preferencesModal: {
              title: "Spravovat preference cookies",
              acceptAllBtn: "Přijmout vše",
              acceptNecessaryBtn: "Odmítnout vše",
              savePreferencesBtn: "Přijmout aktuální výběr",
              closeIconLabel: "Zavřít okno",
              sections: [
                {
                  title: "Používání cookies",
                  description:
                    "Používáme cookies k zajištění základních funkcí webu a ke zlepšení vašeho online zážitku.",
                },
                {
                  title: "Nezbytné cookies",
                  description:
                    "Tyto cookies jsou nezbytné pro správné fungování webových stránek, například pro autentizaci uživatelů.",
                  linkedCategory: CAT_NECESSARY,
                  // Disclosure only. This category is readOnly, and the cookie
                  // must not be gated on consent: refusing it would leave the
                  // device switching between app versions between visits.
                  cookieTable: {
                    headers: {
                      name: "Název",
                      domain: "Služba",
                      description: "Popis",
                      expiration: "Vypršení",
                    },
                    body: [
                      {
                        name: "version-key",
                        domain: "Trendaro",
                        description:
                          "Náhodný identifikátor, díky kterému zařízení dostává konzistentní verzi aplikace. Neslouží k profilování ani sledování.",
                        expiration: "Vyprší po 1 roce",
                      },
                    ],
                  },
                },
                {
                  title: "Analytika",
                  description:
                    "Cookies používané pro analytiku pomáhají sbírat data, která umožňují službám pochopit, jak uživatelé interagují s konkrétní službou. Tyto poznatky umožňují službám zlepšovat obsah a vytvářet lepší funkce, které zlepšují uživatelský zážitek.",
                  linkedCategory: CAT_ANALYTICS,
                  cookieTable: {
                    headers: {
                      name: "Název",
                      domain: "Služba",
                      description: "Popis",
                      expiration: "Vypršení",
                    },
                    body: [
                      {
                        name: "_ga",
                        domain: "Google Analytics",
                        description:
                          'Cookie nastavené službou <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                        expiration: "Vyprší po 12 dnech",
                      },
                      {
                        name: "_gid",
                        domain: "Google Analytics",
                        description:
                          'Cookie nastavené službou <a href="https://business.safety.google/adscookies/">Google Analytics</a>',
                        expiration: "Relace",
                      },
                    ],
                  },
                },
                {
                  title: "Reklama",
                  description:
                    'Google používá cookies pro reklamu, včetně zobrazování a vykreslování reklam, personalizace reklam (v závislosti na vašem nastavení reklam na <a href="https://g.co/adsettings">g.co/adsettings</a>), omezení počtu zobrazení reklamy uživateli a měření efektivity reklam.',
                  linkedCategory: CAT_ADVERTISEMENT,
                },
                {
                  title: "Funkčnost",
                  description:
                    "Cookies používané pro funkčnost umožňují uživatelům interagovat se službou nebo webem pro přístup k základním funkcím. Mezi základní funkce patří například volba jazyka, optimalizace produktů a udržování informací o relaci uživatele.",
                  linkedCategory: CAT_FUNCTIONALITY,
                },
                {
                  title: "Bezpečnost",
                  description:
                    "Cookies používané pro bezpečnost ověřují uživatele, předcházejí podvodům a chrání uživatele při interakci se službou.",
                  linkedCategory: CAT_SECURITY,
                },
                {
                  title: "Více informací",
                  description:
                    'Pro jakékoliv dotazy ohledně zásad používání cookies a vašich možností nás prosím <a href="https://www.example.com/contacts">kontaktujte</a>.',
                },
              ],
            },
          },
        },
      },
    });
  }, []);
};
